/**
 * TanStack Query hooks for AI bill scanning
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  billScanService,
  createBillsFromCandidates,
  scanForBills,
} from '../services/bill-scan-service';
import type { DetectedBillCandidate, ScanBillsInput } from '../types/bill-scan.types';
import { billKeys } from './useBudgetOverview';

export function useScanForBills() {
  return useMutation({
    mutationFn: async (input: ScanBillsInput) => {
      const result = await scanForBills(input);
      if (result.error) {
        const err = new Error(result.error.message) as Error & {
          code?: string;
          retryAfter?: number;
        };
        err.code = result.error.code;
        if (result.error.code === 'PROVIDER_UNAVAILABLE' && 'retryAfter' in result.error) {
          err.retryAfter = result.error.retryAfter;
        }
        throw err;
      }
      return result.data ?? [];
    },
  });
}

export function useCreateBillsFromScan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (candidates: DetectedBillCandidate[]) => {
      const result = await createBillsFromCandidates(candidates);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: billKeys.all }),
      ]);
    },
  });
}

export { billScanService };
